"use client"
import { useState } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"

/**
 * Program-owner form to post (or, within 24h, edit) the one public response
 * to a review. Rendered only for the owner of the claimed / self-run listing.
 */
export function ReviewResponseForm({
  reviewId,
  programName,
  existing,
}: {
  reviewId: string
  programName: string
  /** When set, edit mode (only offered inside the 24h window). */
  existing?: { body: string } | null
}) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [body, setBody] = useState(existing?.body ?? "")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isEditing = !!existing

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/reviews/${reviewId}/response`, {
        method: isEditing ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(typeof data.error === "string" ? data.error : "Failed to post response")
        return
      }
      setOpen(false)
      router.refresh()
    } finally {
      setLoading(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[12px] text-[var(--primary)] underline underline-offset-2 hover:no-underline"
      >
        {isEditing ? "Edit your response" : `Respond publicly as ${programName}`}
      </button>
    )
  }

  return (
    <form onSubmit={submit} className="mt-3 border border-[var(--border)] rounded-lg p-4 space-y-2">
      <p className="text-xs font-semibold">
        {isEditing ? "Edit your response" : "Public response"}
        <span className="font-normal text-[var(--muted-foreground)]"> — posted as {programName}</span>
      </p>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        maxLength={1500}
        placeholder="Your side, factually and professionally. This is public and permanent — one response per review, editable for 24 hours."
        className="w-full rounded-lg border border-[var(--input)] bg-white px-3 py-2 text-sm placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)] resize-y"
      />
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-[var(--muted-foreground)]">
          {body.length}/1500 · Same content rules as reviews. Attempting to identify an anonymous
          reviewer is grounds for removal.
        </p>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={loading || body.trim().length < 10}>
          {loading ? "Posting..." : isEditing ? "Save changes" : "Post response"}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={loading}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
